import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import api, { resolveMedia } from "@/lib/api";
import { Lightbox } from "@/components/Lightbox";
import { ArticleContent } from "@/components/ArticleContent";
import { toHtml } from "@/lib/article";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get("/blog").then((r) => setOthers(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  // SEO per articolo: title, meta description, canonical
  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = post.seo_title || `${post.title} — Giovanna Matino Interior`;
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    if (post.seo_description || post.excerpt) setMeta("description", post.seo_description || post.excerpt);
    let link = document.querySelector('link[rel="canonical"]');
    const canonical = post.canonical_url || `${window.location.origin}/blog/${post.slug}`;
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);
    return () => { document.title = prevTitle; };
  }, [post]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (loading || !post) return <div className="container-x py-24 text-taupe">Caricamento…</div>;

  return (
    <div data-testid={`page-blog-post-${slug}`}>
      <article className="max-w-3xl mx-auto px-6 md:px-0">
        <div className="pt-6 pb-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink transition-colors" data-testid="back-to-blog">
            <ArrowLeft size={14} /> Torna al diario
          </Link>
        </div>

        <header className="pb-12">
          <span className="eyebrow text-gold">{post.category || "Diario"}</span>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.08] mt-4 text-pretty break-words">{post.title}</h1>
          <div className="mt-6 flex items-center gap-3 text-sm text-taupe">
            <span>di {post.author}</span>
            <span>·</span>
            <time>{new Date(post.published_at || post.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</time>
          </div>
          {(post.tags || []).length > 0 && (
            <div className="mt-4 flex gap-3 flex-wrap">
              {post.tags.map((t) => (
                <span key={t} className="text-[10px] tracking-[0.18em] uppercase text-taupe">{t}</span>
              ))}
            </div>
          )}
        </header>

        {post.cover_image && (
          <div className="pb-12">
            <img src={resolveMedia(post.cover_image)} alt={post.title} className="w-full h-[320px] md:h-[480px] object-cover" />
          </div>
        )}

        {/* Renderer UNICO condiviso con l'anteprima admin (ArticleContent + .article-content):
            a parità di larghezza, anteprima e pagina pubblica coincidono.
            Articoli storici in testo semplice: convertiti con la stessa toHtml dell'editor. */}
        <div className="pb-20" data-testid="blog-content">
          <ArticleContent html={toHtml(post.content)} />
        </div>

        {(() => {
          const gal = (post.gallery || []).filter((g) => g.show_in_body !== false && g.url);
          if (!gal.length) return null;
          return (
            <section className="pb-20" data-testid="blog-gallery">
              <span className="eyebrow text-gold">Galleria</span>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                {gal.map((g, i) => (
                  <figure key={i} className="group">
                    <button onClick={() => setLightboxIdx(i)} className="block w-full overflow-hidden" data-testid={`blog-gallery-img-${i}`} aria-label={g.alt || post.title}>
                      <img src={resolveMedia(g.url)} alt={g.alt || post.title} loading="lazy"
                        className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
                    </button>
                    {g.caption && <figcaption className="mt-2 text-xs text-taupe font-sans">{g.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </section>
          );
        })()}
        {lightboxIdx !== null && (
          <Lightbox
            images={(post.gallery || []).filter((g) => g.show_in_body !== false && g.url)}
            index={lightboxIdx} onClose={() => setLightboxIdx(null)} onNavigate={setLightboxIdx} />
        )}
      </article>

      {others.length > 0 && (
        <section className="border-t border-hair bg-white py-20">
          <div className="container-x">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="eyebrow">Continua a leggere</span>
                <h3 className="font-serif text-3xl md:text-4xl mt-3">Altri articoli</h3>
              </div>
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase link-underline">
                Tutti gli articoli <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6 items-start">
              {others.map((o) => (
                <Link key={o.id} to={`/blog/${o.slug}`} className="group flex flex-col text-left" data-testid={`blog-related-${o.slug}`}>
                  <div className="overflow-hidden">
                    <img src={resolveMedia(o.cover_image)} alt={o.title} className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-gold mt-4">{o.category || "Diario"}</span>
                  <h4 className="font-serif text-xl mt-2 leading-snug group-hover:text-gold transition-colors text-pretty">{o.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
